import React from "react";
import { Link } from "react-router-dom";
import { Spin } from "antd";
import { ArrowRightOutlined, CloseCircleOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { Localize } from "components/service";
import StaffManagementConst from "../staff-management-const";
import { STAFF_WORKER_STATUS } from "../helpers/staff-workers-api-helper";
import { getStaffWorkersStatusUrl } from "../helpers/staff-workers-navigation-helper";
import StaffWorkerStatusPopover from "./staff-worker-status-popover";
import StaffWorkerTooltip from "./staff-worker-tooltip";


const isDefined = (value) => value !== null && value !== undefined;
const statusBadges = {
    missedCheckout: { Icon: WarningOutlined, className: "missed", translationPrefix: "STAFF_MANAGEMENT.Workers_MissedCheckout" },
    failedCheckout: { Icon: CloseCircleOutlined, className: "failed", translationPrefix: "STAFF_MANAGEMENT.Workers_FailedCheckout" },
};

const WorkerStatusBadge = ({ id, status, count }) => {
    const { Icon, className, translationPrefix } = statusBadges[ status ];
    const translationKey = `${ translationPrefix }${ count === 1 ? "One" : "Many" }`;
    const badgeClassName = `staff-management-list__workers-badge staff-management-list__workers-${ className }`;
    const content = (
        <>
            <Icon aria-hidden="true" />
            <span className="staff-management-list__workers-badge-count" aria-hidden="true">{ count }</span>
            <span className="visually-hidden"><Localize count={ count }>{ translationKey }</Localize></span>
        </>
    );

    if (id)
    {
        const to = getStaffWorkersStatusUrl(id, STAFF_WORKER_STATUS[ status ]);
        return (
            <StaffWorkerStatusPopover to={ to } translationKey={ translationKey } translationParams={ { count } }>
                <Link to={ to } className={ badgeClassName }>{ content }</Link>
            </StaffWorkerStatusPopover>
        );
    }

    return (
        <StaffWorkerTooltip translationKey={ translationKey } translationParams={ { count } } trigger={ [ "hover", "focus", "click" ] }>
            <button type="button" className={ badgeClassName }>{ content }</button>
        </StaffWorkerTooltip>
    );
};

const StaffManagementWorkersField = ({ id, count, statusCounts, statusLoading = false, statusError = null, onRetry }) => {
    const hasTotal = isDefined(count);
    const counts = hasTotal && !statusLoading && !statusError ? statusCounts : null;
    const hasStatuses = counts && (counts.checkedIn > 0 || counts.missedCheckout > 0 || counts.failedCheckout > 0);
    const workers = (
        <span className="staff-management-list__workers-main">
            { hasStatuses ? (
                <>
                    <span className={ `staff-management-list__on-site${ counts.checkedIn > 0 ? "" : " staff-management-list__on-site--empty" }` }>
                        { counts.checkedIn }
                    </span>
                    <span className="staff-management-list__workers-ratio-total"> / { count }</span>
                </>
            ) : <span className="staff-management-list__workers-total">{ hasTotal ? count : <>&mdash;</> }</span> }
            { hasTotal && statusLoading &&
                <>
                    { " " }
                    <span className="staff-management-list__workers-loading" role="status">
                        <span aria-hidden="true"><Spin size="small" /></span>
                        <span className="visually-hidden"><Localize>STAFF_MANAGEMENT.Workers_StatusesLoading</Localize></span>
                    </span>
                </>
            }
        </span>
    );

    return (
        <div className="staff-management-list__workers-cell">
            { id && hasTotal && count > 0 ? (
                <div className="staff-management-list__workers-view">
                    <Link to={ `${ StaffManagementConst.basePath }/${ id }/workers` }>
                        { workers }
                        <ArrowRightOutlined className="staff-management-list__workers-arrow" aria-hidden="true" />
                        <span className="visually-hidden"> <Localize>STAFF_MANAGEMENT.Action_View</Localize> <Localize>STAFF_MANAGEMENT.Column_Workers</Localize></span>
                    </Link>
                </div>
            ) : workers }
            <span className="staff-management-list__workers-secondary">
                { counts && (counts.missedCheckout > 0 || counts.failedCheckout > 0) &&
                    <span className="staff-management-list__workers-warnings">
                        { counts.missedCheckout > 0 && <WorkerStatusBadge id={ id } status="missedCheckout" count={ counts.missedCheckout } /> }
                        { counts.failedCheckout > 0 && <WorkerStatusBadge id={ id } status="failedCheckout" count={ counts.failedCheckout } /> }
                    </span>
                }
                { hasTotal && statusError && !statusLoading &&
                    <span className="staff-management-list__workers-error">
                        <span role="status"><Localize>STAFF_MANAGEMENT.Workers_StatusesUnavailable</Localize></span>
                        { onRetry &&
                            <button type="button" className="staff-management-list__workers-retry" onClick={ onRetry }>
                                <ReloadOutlined aria-hidden="true" />
                                <span className="visually-hidden"><Localize>STAFF_MANAGEMENT.Action_Retry</Localize></span>
                            </button>
                        }
                    </span>
                }
            </span>
        </div>
    );
};

export default StaffManagementWorkersField;
